import React, { useState, useEffect } from "react";
import { Box, VStack, Flex, useColorMode } from "@chakra-ui/react";
import { Project } from "../../SentimentAnalysis";
import ChannelSearch from "./ChannelSearch";
import ChannelList from "./ChannelList";
import ChannelAnalysis from "./ChannelAnalysis";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../../../../firebase/firebase";
import { getThemeClasses } from "../../../../utils/themeUtils";

export interface ChannelLite {
  id: string;
  name: string;
  organizationId: string;
  projectId: string;
}

interface Props {
  selectedProject: string | null;
  projects: Project[];
  organizationId: string;
  selectedChannelId: string | null;
  users?: Array<{ id: string; firstName?: string; lastName?: string; displayName?: string; name?: string }>;
}

const ChannelDetails: React.FC<Props> = ({
  selectedProject,
  projects,
  organizationId,
  selectedChannelId,
  users = [],
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const project = projects.find((p) => p.id === selectedProject);
  const [channels, setChannels] = useState<ChannelLite[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<ChannelLite | null>(null);

  useEffect(() => {
    if (!selectedProject) return;

    const fetchChannels = async () => {
      const chanRef = collection(
        db,
        `organization/${organizationId}/projects/${selectedProject}/channels`
      );
      const snap = await getDocs(chanRef);
      const arr: ChannelLite[] = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || "Unnamed",
        organizationId: organizationId,
        projectId: selectedProject,
      }));
      setChannels(arr);
    };

    fetchChannels();
  }, [organizationId, selectedProject]);

  useEffect(() => {
    if (selectedChannelId && channels.length > 0) {
      const channel = channels.find(c => c.id === selectedChannelId);
      if (channel) {
        setSelectedChannel(channel);
      }
    } else {
      setSelectedChannel(null);
    }
  }, [selectedChannelId, channels]);

  return (
    <Box
      w="100%"
      px={6}
      py={3}
      mt={5}
      borderWidth="1px"
      borderRadius="lg"
      bg={isDark ? "#1A1C1F" : "white"}
      boxShadow="sm"
      borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
    >
      {project ? (
        <VStack spacing={4} align="start" w="100%">
          <Box w="100%">
            <ChannelAnalysis selectedChannel={selectedChannel} users={users} />
          </Box>
        </VStack>
      ) : (
        <Box textAlign="center">Select a project to view channels.</Box>
      )}
    </Box>
  );
};

export default ChannelDetails;
